import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CrmOrganizerHistoryDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { cn } from '@upme/ui/lib/utils';
import { X } from 'lucide-react';
import { api } from '../../../app/api-client';
import { useAuth } from '../../auth/auth-context';
import { formatCrmEventDate } from '../lib/event-period';
import { CrmCommentsSection } from './crm-comments-section';
import { CrmManagerEventInterestsSection } from './crm-manager-event-interests-section';
import { CRM_PANEL_SURFACE } from './crm-panel-surface';

type Props = { eventId: string; onClose: () => void };
export function CrmOrganizerPanel({ eventId, onClose }: Props) {
  const roles = useAuth().adminRoles;
  const canReadAccount = roles.includes('SUPERADMIN');
  const canOpenEventAdmin = canReadAccount || roles.includes('CITY_MODERATOR');
  const query = useInfiniteQuery({
    queryKey: ['admin', 'crm', 'organizer', eventId],
    initialPageParam: 0,
    gcTime: 0,
    queryFn: ({ pageParam }) =>
      api
        .get(`admin/crm/events/${eventId}/organizer`, { searchParams: { offset: pageParam } })
        .json()
        .then(CrmOrganizerHistoryDto.parse),
    getNextPageParam: (page) => page.nextOffset ?? undefined,
  });
  const first = query.data?.pages[0];
  const organizer = first?.organizer;
  return (
    <aside
      aria-label="Карточка организатора"
      className={cn(
        'flex min-w-0 flex-col bg-background max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:h-dvh',
        CRM_PANEL_SURFACE,
      )}
    >
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border/60 p-5">
        <div className="min-w-0">
          <h2 className="break-words text-xl font-medium">{organizer?.displayName ?? 'Организатор'}</h2>
          {organizer?.username ? (
            <a
              className="text-muted-foreground hover:underline"
              href={`https://t.me/${organizer.username.replace(/^@/, '')}`}
              target="_blank"
              rel="noreferrer"
            >
              @{organizer.username.replace(/^@/, '')}
            </a>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="icon"
          className="size-12 shrink-0 rounded-full bg-muted/50"
          aria-label="Закрыть организатора"
          onClick={onClose}
        >
          <X className="size-6 stroke-[1.75]" />
        </Button>
      </header>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
        {query.isPending ? (
          <p>Загрузка организатора…</p>
        ) : query.isError ? (
          <div>
            <p>Не удалось загрузить организатора.</p>
            <Button variant="outline" onClick={() => void query.refetch()}>
              Повторить
            </Button>
          </div>
        ) : (
          <>
            {organizer?.userId && canReadAccount ? (
              <Link
                to="/users/$userId"
                params={{ userId: organizer.userId }}
                className="text-primary hover:underline"
              >
                Открыть карточку контакта →
              </Link>
            ) : null}
            {organizer?.userId ? <CrmManagerEventInterestsSection contactUserId={organizer.userId} /> : null}
            <CrmCommentsSection target={{ eventId }} />
            <h3 className="font-medium">Все ивенты организатора · {first?.count ?? 0}</h3>
            <ul className="space-y-3">
              {query.data?.pages
                .flatMap((page) => page.items)
                .map((event) => (
                  <li key={event.id} className="rounded-md border border-border/60 p-3">
                    {canOpenEventAdmin ? (
                      <Link
                        to="/events/$eventId"
                        params={{ eventId: event.id }}
                        className="font-medium hover:underline"
                      >
                        {event.title}
                      </Link>
                    ) : (
                      <p className="font-medium">{event.title}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {formatCrmEventDate(event)} · {event.cityName}
                    </p>
                  </li>
                ))}
            </ul>
            {first?.count === 0 ? <p className="text-muted-foreground">Ивентов пока нет.</p> : null}
            {query.hasNextPage ? (
              <Button
                variant="outline"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
              </Button>
            ) : null}
            {query.isFetchNextPageError ? (
              <p role="alert">Не удалось загрузить следующие ивенты. Попробуйте ещё раз.</p>
            ) : null}
          </>
        )}
      </div>
    </aside>
  );
}
